import React from 'react';
import classNames from 'classnames';
import {
  Button,
  CustomExtendedDataField,
  FieldBoolean,
  FieldCheckbox,
  FieldSelect,
  FieldTextInput,
  IconArrowHead,
  IconDelete,
  InlineTextButton,
} from '../../../../components';
import { LISTING_CATEGORY } from '../../../../constants/listing.constants';
import { required, validHin } from '../../../../util/validators';
import BrandDropdownField from '../../../../components/SearchFilters/BrandDropdown/BrandDropdownField';

import css from './EditListingDetailsFields.module.css';
import { FieldArray } from 'react-final-form-arrays';
import { useState } from 'react';
import { Field, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

export const GeneralListingData = props => {
  const { configsByKey, categoryValue, typeValue, formId, intl, isDraft = true } = props;

  return (
    <>
      {/* Editable while the advertisement is still a draft, locked once it is
          published.

          This is the only place the category should change after the first step,
          because `pub_type` sits directly below it and is filtered by it — so
          changing one forces the other to be re-picked. Changing the category from
          the wizard's category step instead would leave a type belonging to the old
          category behind. */}
      <CustomExtendedDataField
        name="pub_category"
        key="pub_category"
        fieldConfig={configsByKey?.category}
        formId={formId}
        disabled={!isDraft}
        defaultRequiredMessage={intl.formatMessage({
          id: 'EditListingDetailsForm.defaultRequiredMessage',
        })}
      />
      <CustomExtendedDataField
        name="pub_type"
        key="pub_type"
        enumFilterValue={categoryValue}
        fieldConfig={configsByKey?.type}
        formId={formId}
        disabled={!isDraft}
        defaultRequiredMessage={intl.formatMessage({
          id: 'EditListingDetailsForm.defaultRequiredMessage',
        })}
        sortOptions
      />
      {categoryValue === LISTING_CATEGORY.EQUIPMENT && (
        <CustomExtendedDataField
          disabled={!typeValue}
          name="pub_equipmentType"
          key="pub_equipmentType"
          enumFilterValue={typeValue}
          fieldConfig={configsByKey?.equipmentType}
          formId={formId}
          defaultRequiredMessage={intl.formatMessage({
            id: 'EditListingDetailsForm.defaultRequiredMessage',
          })}
        />
      )}

      {![LISTING_CATEGORY.EQUIPMENT, LISTING_CATEGORY.MOORING].includes(categoryValue) && (
        <>
          <BrandDropdownField
            name="pub_manufacturer"
            key="pub_manufacturer"
            currentOptionMessageId="EditListingDetailsFields.select"
            formId={formId}
            label={intl.formatMessage({ id: 'Global.manufacturer' })}
          />
        </>
      )}
      <CustomExtendedDataField
        name="pub_model"
        key="pub_model"
        fieldConfig={configsByKey?.model}
        formId={formId}
        disabled={!isDraft}
      />
      {categoryValue === LISTING_CATEGORY.MOORING && (
        <CustomExtendedDataField
          className={css.field}
          name="pub_yearUntil"
          key="pub_yearUntil"
          fieldConfig={configsByKey?.yearUntil}
          formId={formId}
        />
      )}
    </>
  );
};

export const EngineFields = props => {
  const { configsByKey, formId, category = 'boats', formApi } = props;
  const { mutators } = formApi || {};
  const { push } = mutators || {};
  const intl = useIntl();

  return (
    <div className={css.engineFields}>
      <FieldArray name="boatEngines">
        {({ fields }) => {
          return fields.map((name, index) => {
            return (
              <div key={name} className={css.engineContainer}>
                <div className={css.engineHeader}>
                  <div className={css.engineTitle}>
                    <FormattedMessage id="Global.engine" /> #{index + 1}
                  </div>
                  <div>
                    <InlineTextButton
                      className={css.deleteEngineBtn}
                      type="button"
                      onClick={() => fields.remove(index)}
                    >
                      <IconDelete />
                    </InlineTextButton>
                  </div>
                </div>

                <div className={css.fieldsContainer}>
                  <FieldTextInput
                    name={`${name}.engineManufacturer`}
                    label={intl.formatMessage({ id: 'Global.manufacturer' })}
                    id={`${name}.engineManufacturer`}
                    required
                    validate={required(intl.formatMessage({ id: 'Global.required' }))}
                    className={css.engineField}
                    placeholder={intl.formatMessage({
                      id: 'EditListingDetailsForm.manufacturerPlaceholder',
                    })}
                  />
                  <FieldTextInput
                    name={`${name}.serialNumber`}
                    label={`# ${intl.formatMessage({ id: 'EditListingDetailsForm.serialNumber' })}`}
                    id={`${name}.serialNumber`}
                    className={css.engineField}
                    placeholder={intl.formatMessage({
                      id: 'EditListingDetailsForm.serialNumberPlaceholder',
                    })}
                  />
                  <CustomExtendedDataField
                    name={`${name}.engineModel`}
                    key={`${name}.engineModel`}
                    fieldConfig={configsByKey?.engineModel}
                    formId={formId}
                    className={css.engineField}
                  />

                  <CustomExtendedDataField
                    className={css.engineField}
                    name={`${name}.engineType`}
                    key={`${name}.engineType`}
                    enumFilterValue={LISTING_CATEGORY.BOAT_ENGINES}
                    fieldConfig={{
                      ...configsByKey?.type,
                      saveConfig: {
                        label: intl.formatMessage({ id: 'Global.engineType' }),
                        isRequired: false,
                      },
                    }}
                    formId={formId}
                  />

                  <CustomExtendedDataField
                    className={css.engineField}
                    name={`${name}.enginePower`}
                    key={`${name}.enginePower`}
                    fieldConfig={configsByKey?.enginePower}
                    formId={formId}
                  />
                  <CustomExtendedDataField
                    className={css.engineField}
                    name={`${name}.engineHours`}
                    key={`${name}.engineHours`}
                    fieldConfig={configsByKey?.engineHours}
                    formId={formId}
                  />

                  <CustomExtendedDataField
                    name={`${name}.fuelType`}
                    key={`${name}.fuelType`}
                    fieldConfig={configsByKey?.fuelType}
                    formId={formId}
                    className={css.engineField}
                  />
                  <FieldTextInput
                    name={`${name}.capacity`}
                    label={intl.formatMessage({ id: 'EditListingDetailsForm.capacity' })}
                    placeholder={intl.formatMessage({
                      id: 'EditListingDetailsForm.capacityPlaceholder',
                    })}
                    id={`${name}.capacity`}
                    className={css.engineField}
                  />
                  <FieldSelect
                    name={`${name}.strokes`}
                    label={intl.formatMessage({ id: 'EditListingDetailsForm.strokes' })}
                    id={`${name}.strokes`}
                    className={css.engineField}
                  >
                    <option disabled value="">
                      {intl.formatMessage({ id: 'EditListingDetailsForm.strokesPlaceholder' })}
                    </option>
                    <option value="2">2-takt</option>
                    <option value="4">4-takt</option>
                  </FieldSelect>

                  <FieldTextInput
                    name={`${name}.cylinders`}
                    label={intl.formatMessage({ id: 'EditListingDetailsForm.cylinders' })}
                    placeholder={intl.formatMessage({
                      id: 'EditListingDetailsForm.cylindersPlaceholder',
                    })}
                    id={`${name}.cylinders`}
                    className={css.engineField}
                  />
                  <FieldTextInput
                    name={`${name}.engineYearBuilt`}
                    label={intl.formatMessage({ id: 'Global.yearBuilt' })}
                    placeholder={intl.formatMessage({
                      id: 'CustomExtendedDataField.placeholderLong',
                    })}
                    id={`${name}.engineYearBuilt`}
                    className={css.engineField}
                    type="number"
                  />
                </div>
              </div>
            );
          });
        }}
      </FieldArray>
      <Button
        className={css.addEngineBtn}
        onClick={e => {
          e.stopPropagation();
          push?.('boatEngines', {});
        }}
        type="button"
      >
        + <FormattedMessage id="EditListingDetailsForm.addEngine" />
      </Button>
    </div>
  );
};

export const AdditionalDimensionsFields = props => {
  const { configsByKey, formId, category } = props;
  const intl = useIntl();

  const invalidHinMessage = intl.formatMessage({ id: 'EditListingDetailsForm.invalidHinMessage' });

  return (
    <>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_length"
          key="pub_length"
          fieldConfig={configsByKey?.length}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_loa"
          key="pub_loa"
          fieldConfig={configsByKey?.loa}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_draught"
          key="pub_draught"
          fieldConfig={configsByKey?.draught}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_clearance"
          key="pub_clearance"
          fieldConfig={configsByKey?.clearance}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_draughtKeelup"
          key="pub_draughtKeelup"
          fieldConfig={configsByKey?.draughtKeelup}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_headroom"
          key="pub_headroom"
          fieldConfig={configsByKey?.headroom}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_beam"
          key="pub_beam"
          fieldConfig={configsByKey?.beam}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_displacement"
          key="pub_displacement"
          fieldConfig={configsByKey?.displacement}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_passengerCapacity"
          key="pub_passengerCapacity"
          fieldConfig={configsByKey?.passengerCapacity}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_hullMaterial"
          key="pub_hullMaterial"
          fieldConfig={configsByKey?.hullMaterial}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        {category === 'boats' && (
          <CustomExtendedDataField
            className={css.field}
            name="pub_freshWater"
            key="pub_freshWater"
            fieldConfig={configsByKey?.freshWater}
            formId={formId}
          />
        )}
        <CustomExtendedDataField
          className={css.field}
          name="pub_cruisingSpeed"
          key="pub_cruisingSpeed"
          fieldConfig={configsByKey?.cruisingSpeed}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_holding"
          key="pub_holding"
          fieldConfig={configsByKey?.holding}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_maxSpeed"
          key="pub_maxSpeed"
          fieldConfig={configsByKey?.maxSpeed}
          formId={formId}
        />
      </div>
      <CustomExtendedDataField
        className={css.field}
        name="pub_hin"
        key="pub_hin"
        fieldConfig={configsByKey?.hin}
        formId={formId}
        validate={validHin(invalidHinMessage)}
      />
      {category === 'boats' && (
        <CustomExtendedDataField
          className={css.field}
          name="pub_designer"
          key="pub_designer"
          fieldConfig={configsByKey?.designer}
          formId={formId}
        />
      )}
    </>
  );
};

export const OtherInformationFields = props => {
  const { configsByKey, formId, intl, category = 'boats' } = props;

  const invalidHinMessage = intl.formatMessage({
    id: 'EditListingDetailsForm.invalidHinMessage',
  });

  return (
    <>
      <CustomExtendedDataField
        className={css.field}
        name="pub_hin"
        key="pub_hin"
        fieldConfig={configsByKey?.hin}
        formId={formId}
        validate={validHin(invalidHinMessage)}
      />
      {category === 'boats' && (
        <>
          <CustomExtendedDataField
            className={css.field}
            name="pub_designer"
            key="pub_designer"
            fieldConfig={configsByKey?.designer}
            formId={formId}
          />
          <CustomExtendedDataField
            className={css.field}
            name="pub_freshWater"
            key="pub_freshWater"
            fieldConfig={configsByKey?.freshWater}
            formId={formId}
          />
        </>
      )}
      <CustomExtendedDataField
        className={css.field}
        name="pub_holding"
        key="pub_holding"
        fieldConfig={configsByKey?.holding}
        formId={formId}
      />
      <CustomExtendedDataField
        className={css.field}
        name="pub_cruisingSpeed"
        key="pub_cruisingSpeed"
        fieldConfig={configsByKey?.cruisingSpeed}
        formId={formId}
      />
      <CustomExtendedDataField
        className={css.field}
        name="pub_maxSpeed"
        key="pub_maxSpeed"
        fieldConfig={configsByKey?.maxSpeed}
        formId={formId}
      />
    </>
  );
};

export const OtherEngineInformationFields = props => {
  const { configsByKey, formId } = props;

  return (
    <>
      <CustomExtendedDataField
        className={css.field}
        name="pub_serialNumber"
        key="pub_serialNumber"
        fieldConfig={configsByKey?.serialNumber}
        formId={formId}
      />
      <CustomExtendedDataField
        className={css.field}
        name="pub_fuelType"
        key="pub_fuelType"
        fieldConfig={configsByKey?.fuelType}
        formId={formId}
      />
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_capacity"
          key="pub_capacity"
          fieldConfig={configsByKey?.capacity}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_strokes"
          key="pub_strokes"
          fieldConfig={configsByKey?.strokes}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_cylinders"
          key="pub_cylinders"
          fieldConfig={configsByKey?.cylinders}
          formId={formId}
        />
      </div>
      <CustomExtendedDataField
        className={css.field}
        name="pub_power"
        key="pub_power"
        fieldConfig={configsByKey?.enginePower}
        formId={formId}
      />
      <CustomExtendedDataField
        className={css.field}
        name="pub_hours"
        key="pub_hours"
        fieldConfig={configsByKey?.engineHours}
        formId={formId}
      />
    </>
  );
};

export const TrailerDimensions = props => {
  const { configsByKey, formId } = props;
  return (
    <>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_length"
          key="pub_length"
          fieldConfig={configsByKey?.length}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_width"
          key="pub_width"
          fieldConfig={configsByKey?.width}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_axleWidth"
          key="pub_axleWidth"
          fieldConfig={configsByKey?.axleWidth}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_payload"
          key="pub_payload"
          fieldConfig={configsByKey?.payload}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_curbWeight"
          key="pub_curbWeight"
          fieldConfig={configsByKey?.curbWeight}
          formId={formId}
        />
      </div>
    </>
  );
};

export const OtherTrailerInformation = props => {
  const { configsByKey, formId } = props;

  return (
    <>
      <CustomExtendedDataField
        className={css.field}
        name="pub_axles"
        key="pub_axles"
        fieldConfig={configsByKey?.axles}
        formId={formId}
      />
      <CustomExtendedDataField
        className={css.field}
        name="pub_braked"
        key="pub_braked"
        fieldConfig={configsByKey?.braked}
        formId={formId}
      />
      <CustomExtendedDataField
        className={css.field}
        name="pub_material"
        key="pub_material"
        fieldConfig={configsByKey?.material}
        formId={formId}
      />
    </>
  );
};

export const MooringDimensionFields = props => {
  const { configsByKey, formId } = props;

  return (
    <>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_length"
          key="pub_length"
          fieldConfig={configsByKey?.length}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_width"
          key="pub_width"
          fieldConfig={configsByKey?.width}
          formId={formId}
        />
      </div>
      <div className={css.row}>
        <CustomExtendedDataField
          className={css.field}
          name="pub_waterDepth"
          key="pub_waterDepth"
          fieldConfig={configsByKey?.waterDepth}
          formId={formId}
        />
        <CustomExtendedDataField
          className={css.field}
          name="pub_maxBoatDraught"
          key="pub_maxBoatDraught"
          fieldConfig={configsByKey?.maxBoatDraught}
          formId={formId}
        />
      </div>
    </>
  );
};

export const MooringAdditionalInfoFields = props => {
  const { configsByKey, formId } = props;

  return (
    <div className={css.row}>
      <CustomExtendedDataField
        className={css.field}
        name="pub_town"
        key="pub_town"
        fieldConfig={configsByKey?.town}
        formId={formId}
      />
      <CustomExtendedDataField
        className={css.field}
        name="pub_marina"
        key="pub_marina"
        fieldConfig={configsByKey?.marina}
        formId={formId}
      />
    </div>
  );
};

export const MooringAttributeFields = props => {
  const { configsByKey, formId } = props;
  const intl = useIntl();
  const { values } = useFormState();
  const { boatMooringAvailable } = values || {};

  return (
    <div className={css.container}>
      <FieldCheckbox
        name="boatMooringAvailable"
        id="boatMooringAvailable"
        label={intl.formatMessage({ id: 'EditListingDetailsForm.mooringAvailable' })}
      />
      {boatMooringAvailable && (
        <div className={css.gridLgContainer}>
          <FieldTextInput
            name="boatMooring.title"
            label={intl.formatMessage({ id: 'EditListingDetailsForm.mooringTitle' })}
            id="boatMooring.title"
            className={css.field}
            validate={required(intl.formatMessage({ id: 'Global.required' }))}
            placeholder={intl.formatMessage({
              id: 'EditListingDetailsForm.mooringTitlePlaceholder',
            })}
            required
          />
          <CustomExtendedDataField
            className={css.field}
            name="boatMooring.town"
            key="boatMooring.town"
            fieldConfig={configsByKey?.town}
            formId={formId}
          />
          <CustomExtendedDataField
            className={css.field}
            name="boatMooring.marina"
            key="boatMooring.marina"
            fieldConfig={configsByKey?.marina}
            formId={formId}
          />

          <FieldTextInput
            name="boatMooring.yearUntil"
            label={intl.formatMessage({ id: 'Global.yearBuilt' })}
            placeholder={intl.formatMessage({
              id: 'CustomExtendedDataField.placeholderLong',
            })}
            id="boatMooring.yearUntil"
            className={css.field}
            type="number"
          />
          <CustomExtendedDataField
            className={css.field}
            name="boatMooring.waterDepth"
            key="boatMooring.waterDepth"
            fieldConfig={configsByKey?.waterDepth}
            formId={formId}
          />
          <CustomExtendedDataField
            className={css.field}
            name="boatMooring.maxBoatDraught"
            key="boatMooring.maxBoatDraught"
            fieldConfig={configsByKey?.maxBoatDraught}
            formId={formId}
          />

          <CustomExtendedDataField
            className={css.field}
            name="boatMooring.mooringType"
            key="boatMooring.mooringType"
            enumFilterValue={LISTING_CATEGORY.MOORING}
            fieldConfig={{
              ...configsByKey?.type,
              saveConfig: {
                label: intl.formatMessage({ id: 'EditListingDetailsForm.mooringType' }),
                isRequired: false,
              },
            }}
            formId={formId}
          />
        </div>
      )}
    </div>
  );
};
